import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import CenterFocusStrongRoundedIcon from '@mui/icons-material/CenterFocusStrongRounded';
import FullscreenExitRoundedIcon from '@mui/icons-material/FullscreenExitRounded';
import FullscreenRoundedIcon from '@mui/icons-material/FullscreenRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import PauseRoundedIcon from '@mui/icons-material/PauseRounded';
import PhotoCameraRoundedIcon from '@mui/icons-material/PhotoCameraRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import RadioButtonCheckedRoundedIcon from '@mui/icons-material/RadioButtonCheckedRounded';
import StopCircleRoundedIcon from '@mui/icons-material/StopCircleRounded';
import VideocamOffRoundedIcon from '@mui/icons-material/VideocamOffRounded';
import VolumeMuteRoundedIcon from '@mui/icons-material/VolumeMuteRounded';
import VolumeOffRoundedIcon from '@mui/icons-material/VolumeOffRounded';
import VolumeUpRoundedIcon from '@mui/icons-material/VolumeUpRounded';
import {
  Box,
  ButtonBase,
  Chip,
  CircularProgress,
  ClickAwayListener,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Popper,
  Select,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';

const HAT_PLUGIN_BASE_PATH = '/hat-plugin';
const HAT_PLUGIN_SCRIPTS = ['polyfill2.js', 'HATMediaJsPlugin-1.1.1.js', 'jquery-1.12.1.min.js', 'jquery.cookie.js', 'cryptico.min.js', 'uuid.js'];

type HatJsWindowControlCallback = {
  pluginErrorHandler?: (windowNumber: number, code: number, data?: { statusString?: string; subStatusCode?: string | number }) => void;
  onFirstFrameDisplay?: (windowNumber: number, width?: number, height?: number) => void;
};

type HatJsPlugin = {
  HATJS_Play: (url: string, options: { playURL: string }, windowNumber: number, startTime?: string, endTime?: string) => Promise<unknown> | unknown;
  HATJS_Stop: (windowNumber: number) => Promise<unknown> | unknown;
  HATJS_Pause: (windowNumber: number) => Promise<unknown> | unknown;
  HATJS_Resume: (windowNumber: number) => Promise<unknown> | unknown;
  HATJS_GetOSDTime?: (windowNumber: number) => Promise<unknown> | unknown;
  HATJS_StopRealPlayAll?: () => Promise<unknown> | unknown;
  HATJS_OpenSound: (windowNumber: number) => Promise<unknown> | unknown;
  HATJS_CloseSound: (windowNumber: number) => Promise<unknown> | unknown;
  HATJS_CapturePicture?: (windowNumber: number, fileName: string, fileType: 'JPEG' | 'BMP') => Promise<unknown> | unknown;
  HATJS_StartSave?: (windowNumber: number, fileName: string, options?: { iPackage?: number }) => Promise<unknown> | unknown;
  HATJS_StopSave?: (windowNumber: number) => Promise<unknown> | unknown;
  HATJS_Resize?: (width: number, height: number) => Promise<unknown> | unknown;
  HATJS_FullScreenSingle?: (windowNumber: number) => Promise<unknown> | unknown;
  HATJS_SetWindowControlCallback?: (callback: HatJsWindowControlCallback) => void;
  HATJS_DestroyPlugin: () => Promise<unknown> | unknown;
};

type HatJsConstructor = new (options: {
  szId: string;
  iType: number;
  iMode: number;
  iWidth: number;
  iHeight: number;
  iMaxSplit: number;
  iCurrentSplit: number;
  szBasePath: string;
  oStyle: { border: string; borderSelect: string; background: string };
}) => HatJsPlugin;

declare global {
  interface Window {
    HATMediaJsPlugin?: HatJsConstructor;
  }
}

let sdkLoadPromise: Promise<void> | null = null;
let hatJsLifecycleQueue: Promise<void> = Promise.resolve();
let hatJsInstanceSequence = 0;

const CONTROL_WATCHDOG_MS = 5000;
const CONTROL_READINESS_FALLBACK_MS = 5000;
const HATJS_OPERATION_TIMEOUT_MS = 10000;
const HATJS_RECORDING_MAX_DURATION_MS = 2 * 60 * 1000;
const HATJS_PLAYBACK_PLAY_TIMEOUT_MS = 25000;
const HATJS_PLAYBACK_PLAY_MAX_ATTEMPTS = 2;
const HATJS_LIVE_PLAY_TIMEOUT_MS = 22000;
const HATJS_LIVE_PLAY_MAX_ATTEMPTS = 2;
const OSD_POLL_INTERVAL_MS = 500;
const INITIAL_CONTROLS_HIDE_DELAY_MS = 5000;
const CONTROLS_HIDE_DELAY_MS = 5000;

const getHatJsOutlinedControlSx = (overlayControls: boolean) => ({
  borderRadius: '6px',
  width: 28,
  height: 28,
  color: overlayControls ? '#fff' : 'secondary.main',
  border: 1,
  borderColor: overlayControls ? 'rgba(255,255,255,0.55)' : 'secondary.main',
  '&:hover': { backgroundColor: overlayControls ? 'rgba(255,255,255,0.16)' : 'action.hover' },
  '&.Mui-disabled': {
    color: overlayControls ? 'rgba(255,255,255,0.4)' : 'text.disabled',
    borderColor: overlayControls ? 'rgba(255,255,255,0.24)' : 'divider',
  },
});

const getHatJsStatusLabel = (connected: boolean, hasError: boolean, isLive: boolean) => {
  if (connected) return isLive ? 'Ao vivo' : 'Reproduzindo';
  return hasError ? 'Erro' : 'Conectando';
};

const getHatJsStatusDotColor = (connected: boolean, hasError: boolean) => {
  if (connected) return 'success.main';
  return hasError ? 'error.main' : 'text.disabled';
};

const getHatJsAudioIconColor = (overlayControls: boolean, audioEnabled: boolean, audioWasActivated: boolean) => {
  if (audioEnabled) return 'success.main';
  if (audioWasActivated) return 'error.main';
  return overlayControls ? 'rgba(255,255,255,0.85)' : 'text.secondary';
};

const getHatJsAudioLabel = (audioEnabled: boolean, audioWasActivated: boolean) => {
  if (audioEnabled) return 'Silenciar áudio';
  return audioWasActivated ? 'Áudio silenciado — clique para reativar' : 'Ativar áudio';
};

const getHatJsAudioAriaLabel = (audioEnabled: boolean) => (audioEnabled ? 'Silenciar áudio' : 'Ativar áudio');

const getHatJsAudioIcon = (audioEnabled: boolean, audioWasActivated: boolean) => {
  if (audioEnabled) return <VolumeUpRoundedIcon fontSize="small" />;
  return audioWasActivated ? <VolumeMuteRoundedIcon fontSize="small" /> : <VolumeOffRoundedIcon fontSize="small" />;
};

const getHatJsToolbarSx = (overlayControls: boolean, controlsVisible: boolean, hasCustomControls: boolean) => {
  const isHidden = overlayControls && !controlsVisible;
  return {
    position: overlayControls ? 'absolute' : 'static',
    left: overlayControls ? 0 : undefined,
    right: overlayControls ? 0 : undefined,
    bottom: overlayControls ? 0 : undefined,
    zIndex: overlayControls ? 3 : undefined,
    px: { xs: 1, sm: 1.5 },
    py: hasCustomControls ? 0.5 : 1,
    color: overlayControls ? '#fff' : 'text.primary',
    backgroundColor: overlayControls ? 'transparent' : 'background.paper',
    backgroundImage: overlayControls ? 'linear-gradient(to top, rgba(0, 0, 0, 0.88) 0%, rgba(0, 0, 0, 0.58) 58%, rgba(0, 0, 0, 0) 100%)' : 'none',
    opacity: isHidden ? 0 : 1,
    visibility: isHidden ? 'hidden' : 'visible',
    transform: isHidden ? 'translateY(10px)' : 'translateY(0)',
    pointerEvents: 'auto',
    transition: 'opacity 180ms ease, transform 180ms ease, visibility 180ms ease',
    willChange: overlayControls ? 'opacity, transform' : undefined,
    '& .MuiDivider-root': {
      borderColor: overlayControls ? 'rgba(255,255,255,0.28)' : undefined,
    },
    '& .MuiIconButton-root': {
      color: overlayControls ? '#fff' : undefined,
    },
    '& .MuiButtonBase-root': {
      color: overlayControls ? '#fff' : undefined,
      borderColor: overlayControls ? 'rgba(255,255,255,0.55)' : undefined,
    },
    '& .MuiTypography-root': {
      color: overlayControls ? 'rgba(255,255,255,0.88)' : undefined,
    },
  };
};

const describePlayerOperation = (operation: string): string => {
  if (operation.startsWith('Play')) return 'abrir o vídeo';
  if (operation.startsWith('Stop')) return 'parar o vídeo';
  if (operation.startsWith('OpenSound')) return 'ativar o áudio';
  if (operation.startsWith('CloseSound')) return 'silenciar o áudio';
  if (operation.startsWith('DestroyPlugin')) return 'fechar o player';
  return 'executar a ação solicitada';
};

const invokeHatJsWithTimeout = async <T,>(action: () => T | Promise<T>, operation: string, timeoutMs: number = HATJS_OPERATION_TIMEOUT_MS): Promise<T> => {
  let timeoutId: number | null = null;
  try {
    const result = action();
    return await new Promise<T>((resolve, reject) => {
      timeoutId = window.setTimeout(() => reject(new Error(`Não foi possível ${describePlayerOperation(operation)} dentro do tempo esperado.`)), timeoutMs);
      Promise.resolve(result).then(resolve, reject);
    });
  } finally {
    if (timeoutId !== null) window.clearTimeout(timeoutId);
  }
};

const logHatJs = (instanceId: number, event: string, details: Record<string, unknown> = {}) => {
  void instanceId;
  void event;
  void details;
};

const SHORT_ERROR_MAX_LENGTH = 72;

const shortErrorMessage = (message: string): string => {
  const trimmed = message.trim();
  if (trimmed.length <= SHORT_ERROR_MAX_LENGTH) return trimmed;
  return `${trimmed.slice(0, SHORT_ERROR_MAX_LENGTH - 1).replace(/\s+$/, '')}…`;
};

const loadScript = (src: string) =>
  new Promise<void>((resolve, reject) => {
    const existingScript = document.querySelector(`script[data-hatjs-src="${src}"]`);
    if (existingScript) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = src;
    script.async = false;
    script.dataset.hatjsSrc = src;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Não foi possível carregar o player de vídeo: ${src}`));
    document.head.appendChild(script);
  });

const loadHatJsSdk = () => {
  if (sdkLoadPromise === null) {
    sdkLoadPromise = HAT_PLUGIN_SCRIPTS.reduce((promise, script) => promise.then(() => loadScript(`${HAT_PLUGIN_BASE_PATH}/${script}`)), Promise.resolve());
  }
  return sdkLoadPromise;
};

const enqueueHatJsLifecycle = (operation: () => Promise<void> | void): Promise<void> => {
  const queuedOperation = hatJsLifecycleQueue.then(operation);
  hatJsLifecycleQueue = queuedOperation.catch(() => undefined);
  return queuedOperation;
};

const normalizeHatJsError = (reason: unknown, fallback: string): string => {
  const cleanupMessage = (message: string) =>
    message
      .replace(/HATJS\s+Play\(\d+\)\s+falhou\.?/gi, 'Não foi possível abrir o vídeo desta câmera.')
      .replace(
        /HATJS\s+([A-Za-z]+)\(\d+\)\s+excedeu o tempo limite\.?/gi,
        (_match, operation) => `Não foi possível ${describePlayerOperation(operation)} dentro do tempo esperado.`
      )
      .replace(/HATMediaJsPlugin/gi, 'recurso de vídeo')
      .replace(/runtime HATJS/gi, 'player de vídeo')
      .replace(/\bHATJS\b/gi, 'player de vídeo')
      .replace(/\bSDK\b/g, 'serviço de vídeo');

  if (reason instanceof Error && reason.message) return cleanupMessage(reason.message);
  if (typeof reason === 'string' && reason.trim()) return cleanupMessage(reason);
  if (typeof reason === 'number') return `${fallback} (código ${reason})`;
  if (reason === undefined) return `${fallback} (sem resposta do serviço de vídeo)`;
  if (reason === null) return `${fallback} (resposta vazia do serviço de vídeo)`;
  if (reason && typeof reason === 'object') {
    const details = reason as { message?: unknown; statusString?: unknown; subStatusCode?: unknown; code?: unknown };
    const detail = details.message || details.statusString || details.subStatusCode || details.code;
    if (typeof detail === 'string' && detail.trim()) return cleanupMessage(detail);
    if (typeof detail === 'number') return `${fallback} (código ${detail})`;
  }
  return cleanupMessage(fallback);
};

const playerStreamErrorMessage = (reason: unknown): string => {
  const fallback = 'Não foi possível reproduzir o vídeo. Tente novamente.';
  const message = reason instanceof Error ? reason.message : typeof reason === 'string' ? reason : '';

  if (/^HATJS\s+Play\(\d+\)\s+falhou\.?$/i.test(message) || /^HATJS\s+[A-Za-z]+\(\d+\)\s+excedeu o tempo limite\.?$/i.test(message)) {
    return normalizeHatJsError(message, fallback);
  }
  if (reason instanceof Error && /^(Não foi possível|O recurso de vídeo)/i.test(message)) {
    return normalizeHatJsError(reason, fallback);
  }
  return fallback;
};

const toHatPlaybackTime = (value: string) => {
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(value)) return value;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const pad = (part: number) => String(part).padStart(2, '0');
  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())} ${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}:${pad(
    date.getUTCSeconds()
  )}`;
};

const parseHatJsOsdTime = (value: unknown): Date | null => {
  let timestampValue: unknown = value;
  if (value && typeof value === 'object') {
    const osd = value as { szTimeStamp?: unknown; iTime?: unknown };
    timestampValue = typeof osd.szTimeStamp === 'string' && osd.szTimeStamp.trim() ? osd.szTimeStamp : osd.iTime;
  }

  if (typeof timestampValue === 'number' && Number.isFinite(timestampValue)) {
    const timestamp = timestampValue < 1_000_000_000_000 ? timestampValue * 1000 : timestampValue;
    const date = new Date(timestamp);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  if (typeof timestampValue !== 'string' || !timestampValue.trim()) return null;

  const normalized = timestampValue.trim().replace(' ', 'T');
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
};

// Os limites de gravação (recordingStartedAt/EndedAt) chegam em hora de parede do dispositivo,
// no mesmo formato do OSD ("YYYY-MM-DD HH:mm:ss" ou ISO). Parseamos EXATAMENTE como o OSD para
// que as comparações fiquem na mesma escala — só usamos a ordem/diferença entre os valores, então
// o fuso do navegador se cancela e não distorce a sincronização.
const parsePlaybackWallClock = (value: string | undefined): number | null => {
  if (!value || !value.trim()) return null;
  const date = new Date(value.trim().replace(' ', 'T'));
  const ms = date.getTime();
  return Number.isNaN(ms) ? null : ms;
};

const getPlaybackMaster = (streams: HatJsStream[]): { stream: HatJsStream; windowNumber: number; startedAtMs: number } | null =>
  streams.reduce<{ stream: HatJsStream; windowNumber: number; startedAtMs: number } | null>((earliest, stream, windowNumber) => {
    if (!stream.playback || stream.playback.inactive) return earliest;
    const startedAtMs = parsePlaybackWallClock(stream.playback.startedAt);
    const comparableStartedAtMs = startedAtMs ?? Number.MAX_SAFE_INTEGER;
    if (!earliest || comparableStartedAtMs < earliest.startedAtMs) {
      return { stream, windowNumber, startedAtMs: comparableStartedAtMs };
    }
    return earliest;
  }, null);

export interface HatJsPlaybackRange {
  startedAt: string;
  endedAt: string;
  inactive?: boolean;
  recordingStartedAt?: string;
  recordingEndedAt?: string;
}

export interface HatJsStream {
  channelId: number;
  title: string;
  url: string;
  playback?: HatJsPlaybackRange;
}

export interface HatJsVideoPlayerHandle {
  restartLive: (streams: HatJsStream[], quality?: HatJsVideoQuality) => Promise<boolean>;
  restartPlayback: (streams: HatJsStream[]) => Promise<boolean>;
}

export type HatJsVideoQuality = 'SUB' | 'HIGH';

interface HatJsVideoPlayerProps {
  streams: HatJsStream[];
  title: string;
  quality: HatJsVideoQuality;
  qualityChanging?: boolean;
  qualityChangeDisabled?: boolean;
  showQualityControl?: boolean;
  preserveVideoAspectRatio?: boolean;
  overlayControls?: boolean;
  enableSpotlightMode?: boolean;
  controls?: React.ReactNode;
  onQualityChange: (quality: HatJsVideoQuality) => void | Promise<void>;
  onOsdTime?: (date: Date) => void;
  onFullscreenToggle?: (isFullscreen: boolean) => void;
  isFullscreen?: boolean;
}

interface HatJsVideoOverlayProps {
  streams: HatJsStream[];
  gridColumns: number;
  gridRows: number;
  isPlayback: boolean;
  outOfWindowCells: Record<number, boolean>;
  audioEnabledWindow: number | null;
  audioPending: boolean;
  recordingWindow: number | null;
  recordingPending: boolean;
  ready: boolean;
  enableSpotlightMode: boolean;
  spotlightIsActive: boolean;
  spotlightWindowIndex: number;
  onAudioWindowToggle: (windowNumber: number) => void;
  onSpotlightChange: (channelId: number | null) => void;
  onWindowFullscreen: (windowNumber: number) => void;
  onWindowSnapshot: (windowNumber: number) => void;
  onWindowRecordToggle: (windowNumber: number) => void;
}

const HatJsVideoOverlay: React.FC<HatJsVideoOverlayProps> = ({
  streams,
  gridColumns,
  gridRows,
  isPlayback,
  outOfWindowCells,
  audioEnabledWindow,
  audioPending,
  recordingWindow,
  recordingPending,
  ready,
  enableSpotlightMode,
  spotlightIsActive,
  spotlightWindowIndex,
  onAudioWindowToggle,
  onSpotlightChange,
  onWindowFullscreen,
  onWindowSnapshot,
  onWindowRecordToggle,
}) => {
  const theme = useTheme();
  const canUseSpotlight = enableSpotlightMode && streams.length > 1;
  const activeSpotlightStream = spotlightIsActive ? streams[spotlightWindowIndex] : null;
  const cellCount = Math.max(gridColumns * gridRows, streams.length);

  return (
    <>
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          zIndex: 1,
          display: 'grid',
          gridTemplateColumns: `repeat(${gridColumns}, 1fr)`,
          gridTemplateRows: `repeat(${gridRows}, 1fr)`,
          boxSizing: 'border-box',
        }}
      >
        {(spotlightIsActive ? [] : Array.from({ length: cellCount })).map((_, index) => {
          const stream = streams[index];
          const isEmptyCell = !stream;
          const audioActiveInCell = audioEnabledWindow === index;
          const cellOutOfWindow = Boolean(stream) && isPlayback && Boolean(outOfWindowCells[index]);
          const cellInactive = Boolean(stream?.playback?.inactive);
          const showNoVideo = isEmptyCell || cellOutOfWindow || cellInactive;
          const audioDisabled = !ready || audioPending || cellOutOfWindow;
          const snapshotButtonRight = canUseSpotlight ? 70 : 38;
          const recordButtonRight = canUseSpotlight ? 102 : 70;
          const recordingActiveInCell = recordingWindow === index;
          const recordDisabled = !ready || recordingPending || cellOutOfWindow || (recordingWindow !== null && !recordingActiveInCell);
          return (
            <Box
              key={stream?.channelId ?? `empty-${index}`}
              sx={{
                position: 'relative',
                boxSizing: 'border-box',
              }}
            >
              {showNoVideo && (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: theme.palette.common.black,
                  }}
                >
                  <VideocamOffRoundedIcon sx={{ fontSize: 28, color: alpha(theme.palette.common.white, 0.42) }} />
                </Box>
              )}
              {stream && ready && !showNoVideo && (
                <>
                  <Tooltip title={audioActiveInCell ? 'Silenciar áudio desta câmera' : `Ouvir ${stream.title}`}>
                    <Box
                      component="span"
                      sx={{
                        position: 'absolute',
                        top: 6,
                        right: 6,
                        zIndex: 2,
                        width: 26,
                        height: 26,
                        pointerEvents: 'auto',
                      }}
                    >
                      <IconButton
                        size="small"
                        aria-label={audioActiveInCell ? 'Silenciar áudio desta câmera' : `Ouvir ${stream.title}`}
                        disabled={audioDisabled}
                        onClick={event => {
                          event.stopPropagation();
                          onAudioWindowToggle(index);
                        }}
                        sx={{
                          boxSizing: 'border-box',
                          width: 26,
                          height: 26,
                          p: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: audioActiveInCell ? 'success.main' : '#fff',
                          backgroundColor: audioActiveInCell ? 'rgba(0,0,0,0.64)' : 'rgba(0,0,0,0.48)',
                          border: '1px solid',
                          borderColor: audioActiveInCell ? alpha(theme.palette.success.main, 0.7) : 'rgba(255,255,255,0.28)',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.32)',
                          '&:hover': {
                            backgroundColor: 'rgba(0,0,0,0.68)',
                            borderColor: audioActiveInCell ? theme.palette.success.main : 'rgba(255,255,255,0.55)',
                          },
                          '&.Mui-disabled': {
                            color: 'rgba(255,255,255,0.35)',
                            borderColor: 'rgba(255,255,255,0.16)',
                            backgroundColor: 'rgba(0,0,0,0.28)',
                          },
                        }}
                      >
                        {audioActiveInCell ? <VolumeUpRoundedIcon sx={{ fontSize: 15 }} /> : <VolumeOffRoundedIcon sx={{ fontSize: 15 }} />}
                      </IconButton>
                    </Box>
                  </Tooltip>
                  {canUseSpotlight && (
                    <Tooltip title={`Abrir ${stream.title} em tela cheia`}>
                      <IconButton
                        size="small"
                        aria-label={`Abrir ${stream.title} em tela cheia`}
                        onClick={event => {
                          event.stopPropagation();
                          onWindowFullscreen(index);
                        }}
                        sx={{
                          position: 'absolute',
                          top: 6,
                          right: 38,
                          zIndex: 2,
                          boxSizing: 'border-box',
                          width: 26,
                          height: 26,
                          p: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          pointerEvents: 'auto',
                          color: '#fff',
                          backgroundColor: 'rgba(0,0,0,0.48)',
                          border: '1px solid',
                          borderColor: 'rgba(255,255,255,0.28)',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.32)',
                          '&:hover': {
                            backgroundColor: 'rgba(0,0,0,0.68)',
                            borderColor: 'rgba(255,255,255,0.55)',
                          },
                        }}
                      >
                        <CenterFocusStrongRoundedIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title={`Capturar imagem de ${stream.title}`}>
                    <IconButton
                      size="small"
                      aria-label={`Capturar imagem de ${stream.title}`}
                      onClick={event => {
                        event.stopPropagation();
                        onWindowSnapshot(index);
                      }}
                      sx={{
                        position: 'absolute',
                        top: 6,
                        right: snapshotButtonRight,
                        zIndex: 2,
                        boxSizing: 'border-box',
                        width: 26,
                        height: 26,
                        p: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        pointerEvents: 'auto',
                        color: '#fff',
                        backgroundColor: 'rgba(0,0,0,0.48)',
                        border: '1px solid',
                        borderColor: 'rgba(255,255,255,0.28)',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.32)',
                        '&:hover': {
                          backgroundColor: 'rgba(0,0,0,0.68)',
                          borderColor: 'rgba(255,255,255,0.55)',
                        },
                      }}
                    >
                      <PhotoCameraRoundedIcon sx={{ fontSize: 15 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={recordingActiveInCell ? `Parar gravação de ${stream.title}` : `Gravar ${stream.title}`}>
                    <IconButton
                      size="small"
                      aria-label={recordingActiveInCell ? `Parar gravação de ${stream.title}` : `Gravar ${stream.title}`}
                      disabled={recordDisabled}
                      onClick={event => {
                        event.stopPropagation();
                        onWindowRecordToggle(index);
                      }}
                      sx={{
                        position: 'absolute',
                        top: 6,
                        right: recordButtonRight,
                        zIndex: 2,
                        boxSizing: 'border-box',
                        width: 26,
                        height: 26,
                        p: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        pointerEvents: 'auto',
                        color: recordingActiveInCell ? 'error.main' : '#fff',
                        backgroundColor: recordingActiveInCell ? 'rgba(0,0,0,0.68)' : 'rgba(0,0,0,0.48)',
                        border: '1px solid',
                        borderColor: recordingActiveInCell ? alpha(theme.palette.error.main, 0.72) : 'rgba(255,255,255,0.28)',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.32)',
                        '&:hover': {
                          backgroundColor: 'rgba(0,0,0,0.72)',
                          borderColor: recordingActiveInCell ? theme.palette.error.main : 'rgba(255,255,255,0.55)',
                        },
                        '@keyframes recordingPulse': {
                          '0%, 100%': {
                            opacity: 1,
                            transform: 'scale(1)',
                          },
                          '50%': {
                            opacity: 0.42,
                            transform: 'scale(0.82)',
                          },
                        },
                        '&.Mui-disabled': {
                          color: 'rgba(255,255,255,0.35)',
                          borderColor: 'rgba(255,255,255,0.16)',
                          backgroundColor: 'rgba(0,0,0,0.28)',
                        },
                      }}
                    >
                      {recordingActiveInCell ? (
                        <StopCircleRoundedIcon sx={{ fontSize: 16, animation: 'recordingPulse 1.2s ease-in-out infinite' }} />
                      ) : (
                        <RadioButtonCheckedRoundedIcon sx={{ fontSize: 15 }} />
                      )}
                    </IconButton>
                  </Tooltip>
                </>
              )}
            </Box>
          );
        })}
      </Box>

      {canUseSpotlight && spotlightIsActive && activeSpotlightStream && (
        <Stack direction="row" spacing={0.75} sx={{ position: 'absolute', top: 12, right: 12, zIndex: 2 }}>
          <Tooltip title="Voltar para grade">
            <IconButton
              size="small"
              aria-label="Voltar para grade"
              onClick={event => {
                event.stopPropagation();
                onSpotlightChange(null);
              }}
              sx={{
                width: 30,
                height: 30,
                color: '#fff',
                backgroundColor: alpha(theme.palette.secondary.main, 0.92),
                border: '1px solid',
                borderColor: alpha(theme.palette.secondary.light, 0.8),
                boxShadow: '0 2px 8px rgba(0,0,0,0.32)',
                '&:hover': {
                  backgroundColor: theme.palette.secondary.main,
                  borderColor: 'rgba(255,255,255,0.55)',
                },
              }}
            >
              <FullscreenExitRoundedIcon sx={{ fontSize: 17 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title={audioEnabledWindow === spotlightWindowIndex ? 'Silenciar áudio desta câmera' : `Ouvir ${activeSpotlightStream.title}`}>
            <span>
              <IconButton
                size="small"
                aria-label={audioEnabledWindow === spotlightWindowIndex ? 'Silenciar áudio desta câmera' : `Ouvir ${activeSpotlightStream.title}`}
                disabled={!ready || audioPending}
                onClick={event => {
                  event.stopPropagation();
                  onAudioWindowToggle(spotlightWindowIndex);
                }}
                sx={{
                  width: 30,
                  height: 30,
                  color: audioEnabledWindow === spotlightWindowIndex ? 'success.main' : '#fff',
                  backgroundColor: 'rgba(0,0,0,0.64)',
                  border: '1px solid',
                  borderColor: audioEnabledWindow === spotlightWindowIndex ? alpha(theme.palette.success.main, 0.7) : 'rgba(255,255,255,0.28)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.32)',
                  '&:hover': {
                    backgroundColor: 'rgba(0,0,0,0.72)',
                    borderColor: audioEnabledWindow === spotlightWindowIndex ? theme.palette.success.main : 'rgba(255,255,255,0.55)',
                  },
                  '&.Mui-disabled': {
                    color: 'rgba(255,255,255,0.35)',
                    borderColor: 'rgba(255,255,255,0.16)',
                    backgroundColor: 'rgba(0,0,0,0.28)',
                  },
                }}
              >
                {audioEnabledWindow === spotlightWindowIndex ? <VolumeUpRoundedIcon sx={{ fontSize: 16 }} /> : <VolumeOffRoundedIcon sx={{ fontSize: 16 }} />}
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      )}
    </>
  );
};

const useHatJsSpotlight = (enabled: boolean, streams: HatJsStream[], split: number) => {
  const [channelId, setChannelId] = useState<number | null>(null);
  const index = enabled ? streams.findIndex(stream => stream.channelId === channelId) : -1;
  const isActive = index >= 0;
  const column = isActive ? index % split : 0;
  const row = isActive ? Math.floor(index / split) : 0;
  const originX = split > 1 ? (column / (split - 1)) * 100 : 50;
  const originY = split > 1 ? (row / (split - 1)) * 100 : 50;

  useEffect(() => {
    if (!enabled || channelId === null) return;
    if (!streams.some(stream => stream.channelId === channelId)) setChannelId(null);
  }, [channelId, enabled, streams]);

  useEffect(() => {
    if (enabled || channelId === null) return;
    setChannelId(null);
  }, [channelId, enabled]);

  return {
    index,
    isActive,
    transform: isActive ? `scale(${split})` : 'none',
    origin: isActive ? `${originX}% ${originY}%` : 'center center',
    willChange: isActive ? 'transform' : undefined,
    setChannelId,
  };
};

interface HatJsQualityControlProps {
  show: boolean;
  quality: HatJsVideoQuality;
  qualityChanging: boolean;
  qualityChangeDisabled: boolean;
  ready: boolean;
  overlayControls: boolean;
  instanceId: number;
  onQualityChange: (quality: HatJsVideoQuality) => void | Promise<void>;
}

const HatJsQualityControl: React.FC<HatJsQualityControlProps> = ({
  show,
  quality,
  qualityChanging,
  qualityChangeDisabled,
  ready,
  overlayControls,
  instanceId,
  onQualityChange,
}) => {
  if (!show) return null;
  return (
    <>
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flex: '0 0 auto' }} role="group" aria-label="Qualidade do vídeo">
        <Tooltip title={`Trocar para ${quality === 'HIGH' ? 'SD' : 'HD'}`}>
          <span>
            <ButtonBase
              aria-label={`Trocar qualidade para ${quality === 'HIGH' ? 'SD' : 'HD'}`}
              disabled={!ready || qualityChanging || qualityChangeDisabled}
              onClick={() => {
                const nextQuality: HatJsVideoQuality = quality === 'HIGH' ? 'SUB' : 'HIGH';
                logHatJs(instanceId, 'quality:requested', {
                  from: quality,
                  fromDisplayQuality: quality === 'HIGH' ? 'HD' : 'SD',
                  to: nextQuality,
                  toDisplayQuality: nextQuality === 'HIGH' ? 'HD' : 'SD',
                  streamType: nextQuality === 'HIGH' ? 0 : 1,
                });
                void onQualityChange(nextQuality);
              }}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: 27,
                minWidth: 38,
                px: 1,
                borderRadius: '6px',
                border: 1,
                borderColor: overlayControls ? 'rgba(255,255,255,0.55)' : 'divider',
                color: overlayControls ? '#fff' : 'text.primary',
                backgroundColor: overlayControls ? 'rgba(0,0,0,0.48)' : 'transparent',
                fontSize: '0.6875rem',
                fontWeight: 700,
                lineHeight: 1,
                transition: theme => theme.transitions.create(['background-color', 'border-color']),
                '&:hover': {
                  backgroundColor: overlayControls ? 'rgba(255,255,255,0.16)' : 'action.hover',
                  borderColor: overlayControls ? '#fff' : 'text.secondary',
                },
                '&.Mui-disabled': {
                  opacity: qualityChanging ? 1 : 0.4,
                  color: overlayControls ? 'rgba(255,255,255,0.45)' : 'text.disabled',
                  borderColor: overlayControls ? 'rgba(255,255,255,0.24)' : 'divider',
                },
              }}
            >
              {qualityChanging ? <CircularProgress size={14} sx={{ color: overlayControls ? '#fff' : 'text.secondary' }} /> : quality === 'HIGH' ? 'HD' : 'SD'}
            </ButtonBase>
          </span>
        </Tooltip>
      </Stack>
    </>
  );
};

export const HatJsVideoPlayer = forwardRef<HatJsVideoPlayerHandle, HatJsVideoPlayerProps>(
  (
    {
      streams,
      title,
      quality,
      qualityChanging = false,
      qualityChangeDisabled = false,
      showQualityControl = true,
      preserveVideoAspectRatio = false,
      overlayControls = false,
      enableSpotlightMode = false,
      controls,
      onQualityChange,
      onOsdTime,
      onFullscreenToggle,
      isFullscreen = false,
    },
    ref
  ) => {
    const theme = useTheme();
    const rootRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const pluginRef = useRef<HatJsPlugin | null>(null);
    useEffect(() => {
      const handleFullscreenChange = () => {
        const fullscreenElement = document.fullscreenElement || (document as any).webkitFullscreenElement;
        const root = rootRef.current;
        if (!fullscreenElement || !root || !window.matchMedia('(pointer: coarse)').matches) return;
        if (!fullscreenElement.contains(root) && !root.contains(fullscreenElement)) return;

        const orientation = screen.orientation;
        if (typeof orientation?.lock === 'function') {
          void orientation.lock('landscape').catch(() => undefined);
        }
      };

      document.addEventListener('fullscreenchange', handleFullscreenChange);
      document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
      return () => {
        document.removeEventListener('fullscreenchange', handleFullscreenChange);
        document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      };
    }, []);
    const instanceId = useRef(++hatJsInstanceSequence).current;
    const containerId = `hatjs-video-player-${instanceId}`;
    const qualityRef = useRef(quality);
    useEffect(() => {
      qualityRef.current = quality;
    }, [quality]);
    const streamKey = streams.map(stream => `${stream.channelId}:${stream.title}:${Boolean(stream.playback)}`).join('|');
    const stableStreams = useMemo(() => streams, [streamKey]);
    const activeStreamsRef = useRef(stableStreams);
    useEffect(() => {
      activeStreamsRef.current = stableStreams;
    }, [stableStreams]);
    const split = Math.max(1, Math.ceil(Math.sqrt(stableStreams.length)));
    const gridColumns = split;
    const gridRows = split;
    const spotlight = useHatJsSpotlight(enableSpotlightMode, stableStreams, split);

    useEffect(() => {
      if (isFullscreen) spotlight.setChannelId(null);
    }, [isFullscreen, spotlight]);

    const playbackMaster = getPlaybackMaster(stableStreams);
    const playbackStream = playbackMaster?.stream;
    const playbackMasterWindow = playbackMaster?.windowNumber ?? 0;
    const isPlayback = Boolean(playbackStream?.playback);
    const [status, setStatus] = useState('Carregando player de vídeo...');
    const [error, setError] = useState<string | null>(null);
    const [audioError, setAudioError] = useState<string | null>(null);
    const [ready, setReady] = useState(false);
    const [audioWindow, setAudioWindow] = useState(0);
    const [audioEnabledWindow, setAudioEnabledWindow] = useState<number | null>(null);
    const [audioPending, setAudioPending] = useState(false);
    const [audioWasActivated, setAudioWasActivated] = useState(false);
    const [recordingWindow, setRecordingWindow] = useState<number | null>(null);
    const [recordingPending, setRecordingPending] = useState(false);
    const recordingWindowRef = useRef<number | null>(null);
    const recordingOperationRef = useRef(false);
    const recordingTimeoutRef = useRef<number | null>(null);
    useEffect(() => {
      recordingWindowRef.current = recordingWindow;
    }, [recordingWindow]);
    const [playbackPaused, setPlaybackPaused] = useState(false);
    const playbackPausedRef = useRef(false);
    const controlRequestRef = useRef(0);
    const controlPendingRef = useRef(false);
    const [controlPending, setControlPending] = useState(false);
    const [audioAnchorEl, setAudioAnchorEl] = useState<HTMLElement | null>(null);
    const audioPopoverContainer = audioAnchorEl
      ? ((audioAnchorEl.ownerDocument.fullscreenElement ?? audioAnchorEl.ownerDocument.body) as HTMLElement)
      : undefined;
    const playbackGenerationRef = useRef(0);
    const liveQualitySwitchGenerationRef = useRef<number | null>(null);
    const [framesDisplayed, setFramesDisplayed] = useState<Record<number, boolean>>({});
    const deferredWindowsRef = useRef<Map<number, number>>(new Map());
    const [outOfWindowCells, setOutOfWindowCells] = useState<Record<number, boolean>>({});
    const isInitializing = !error && !ready;
    const isLive = !isPlayback;
    const [controlsVisible, setControlsVisible] = useState(true);
    const controlsHideTimerRef = useRef<number | null>(null);
    const initialControlsDisplayRef = useRef(true);

    const clearControlsHideTimer = useCallback(() => {
      if (controlsHideTimerRef.current !== null) {
        window.clearTimeout(controlsHideTimerRef.current);
        controlsHideTimerRef.current = null;
      }
    }, []);

    const scheduleControlsHide = useCallback(() => {
      if (!overlayControls) return;
      clearControlsHideTimer();
      const isInitialDisplay = initialControlsDisplayRef.current;
      const hideDelay = isInitialDisplay ? INITIAL_CONTROLS_HIDE_DELAY_MS : CONTROLS_HIDE_DELAY_MS;
      controlsHideTimerRef.current = window.setTimeout(() => {
        setControlsVisible(false);
        initialControlsDisplayRef.current = false;
        controlsHideTimerRef.current = null;
      }, hideDelay);
    }, [clearControlsHideTimer, overlayControls]);

    const revealControls = useCallback(() => {
      if (!overlayControls) return;
      setControlsVisible(true);
      scheduleControlsHide();
    }, [overlayControls, scheduleControlsHide]);

    const revealControlsFromPointer = useCallback(
      (clientX: number, clientY: number) => {
        if (!overlayControls) return;
        const root = rootRef.current;
        if (!root) return;

        const rect = root.getBoundingClientRect();
        const isPointerInsidePlayer = clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom;
        if (isPointerInsidePlayer) revealControls();
      },
      [overlayControls, revealControls]
    );

    const keepControlsVisible = useCallback(
      (event: React.MouseEvent) => {
        event.stopPropagation();
        if (!overlayControls) return;
        clearControlsHideTimer();
        setControlsVisible(true);
      },
      [clearControlsHideTimer, overlayControls]
    );

    useEffect(() => {
      if (!overlayControls) {
        initialControlsDisplayRef.current = true;
        setControlsVisible(true);
        return undefined;
      }
      if (!ready) {
        clearControlsHideTimer();
        setControlsVisible(false);
        return undefined;
      }
      initialControlsDisplayRef.current = true;
      setControlsVisible(true);
      scheduleControlsHide();
      return clearControlsHideTimer;
    }, [clearControlsHideTimer, overlayControls, ready, scheduleControlsHide]);

    useEffect(() => {
      if (!overlayControls || !ready) return undefined;

      const handlePointerMove = (event: MouseEvent) => {
        revealControlsFromPointer(event.clientX, event.clientY);
      };
      const handleTouchStart = (event: TouchEvent) => {
        const touch = event.touches[0];
        if (touch) revealControlsFromPointer(touch.clientX, touch.clientY);
      };

      document.addEventListener('mousemove', handlePointerMove, true);
      document.addEventListener('touchstart', handleTouchStart, true);

      return () => {
        document.removeEventListener('mousemove', handlePointerMove, true);
        document.removeEventListener('touchstart', handleTouchStart, true);
      };
    }, [overlayControls, ready, revealControlsFromPointer]);

    const activateSound = async (requestedWindow?: number) => {
      const plugin = pluginRef.current;
      const targetWindow = Math.min(requestedWindow ?? audioWindow, Math.max(stableStreams.length - 1, 0));
      if (!plugin || !stableStreams[targetWindow]) {
        setAudioError('Aguarde a conexão das câmeras antes de ativar o áudio.');
        return;
      }

      setAudioWindow(targetWindow);
      setAudioPending(true);
      setAudioError(null);
      try {
        await invokeHatJsWithTimeout(() => plugin.HATJS_OpenSound(targetWindow), `OpenSound(${targetWindow})`);
        setAudioEnabledWindow(targetWindow);
        setAudioWasActivated(true);
        setStatus(`Áudio habilitado: ${stableStreams[targetWindow].title}`);
      } catch (reason) {
        setAudioEnabledWindow(null);
        setAudioError(normalizeHatJsError(reason, 'Não foi possível ativar o áudio. Confirme que a câmera já está conectada e tente novamente.'));
      } finally {
        setAudioPending(false);
      }
    };

    const changeAudioWindow = async (nextWindow: number) => {
      const targetWindow = Math.max(0, Math.min(nextWindow, Math.max(stableStreams.length - 1, 0)));
      setAudioWindow(targetWindow);

      if (audioEnabledWindow === null || audioEnabledWindow === targetWindow) return;

      const plugin = pluginRef.current;
      const previousWindow = audioEnabledWindow;
      if (!ready || !plugin || !stableStreams[targetWindow]) return;

      setAudioPending(true);
      setAudioError(null);
      try {
        await invokeHatJsWithTimeout(() => plugin.HATJS_CloseSound(previousWindow), `CloseSound(${previousWindow})`);
        await invokeHatJsWithTimeout(() => plugin.HATJS_OpenSound(targetWindow), `OpenSound(${targetWindow})`);
        setAudioEnabledWindow(targetWindow);
        setAudioWasActivated(true);
        setStatus(`Áudio habilitado: ${stableStreams[targetWindow].title}`);
      } catch (reason) {
        setAudioEnabledWindow(null);
        setAudioError(normalizeHatJsError(reason, 'Não foi possível trocar a câmera de áudio.'));
      } finally {
        setAudioPending(false);
      }
    };

    const muteSound = async () => {
      const plugin = pluginRef.current;
      if (!ready || !plugin || audioEnabledWindow === null) return;

      setAudioPending(true);
      try {
        await invokeHatJsWithTimeout(() => plugin.HATJS_CloseSound(audioEnabledWindow), `CloseSound(${audioEnabledWindow})`);
        setAudioEnabledWindow(null);
        setAudioError(null);
        setStatus('Áudio silenciado');
      } catch (reason) {
        setAudioError(normalizeHatJsError(reason, 'Não foi possível silenciar o áudio.'));
      } finally {
        setAudioPending(false);
      }
    };

    const toggleSound = () => {
      void (audioEnabledWindow === null ? activateSound() : muteSound());
    };

    const toggleAudioWindow = (targetWindow: number) => {
      setAudioWindow(targetWindow);
      if (audioEnabledWindow === targetWindow) {
        void muteSound();
        return;
      }
      if (audioEnabledWindow === null) {
        void activateSound(targetWindow);
        return;
      }
      void changeAudioWindow(targetWindow);
    };

    const openWindowFullscreen = (targetWindow: number) => {
      const plugin = pluginRef.current;
      spotlight.setChannelId(null);
      setError(null);

      if (!plugin?.HATJS_FullScreenSingle) {
        setError('O player não permite abrir esta câmera em tela cheia.');
        return;
      }

      void invokeHatJsWithTimeout(() => plugin.HATJS_FullScreenSingle?.(targetWindow), `FullScreenSingle(${targetWindow})`).catch(reason => {
        setError(normalizeHatJsError(reason, 'Não foi possível abrir esta câmera em tela cheia.'));
      });
    };

    const captureWindowSnapshot = (targetWindow: number) => {
      const plugin = pluginRef.current;
      const stream = stableStreams[targetWindow];
      if (!ready || !plugin || !stream) return;
      setError(null);

      if (!plugin.HATJS_CapturePicture) {
        setError('O player não permite capturar imagem desta câmera.');
        return;
      }

      const safeTitle = stream.title
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-zA-Z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .toLowerCase();
      const timestamp = new Date()
        .toISOString()
        .replace(/[-:T.Z]/g, '')
        .slice(0, 14);
      const fileName = `camera-${safeTitle || stream.channelId}-${timestamp}`;

      void invokeHatJsWithTimeout(() => plugin.HATJS_CapturePicture?.(targetWindow, fileName, 'JPEG'), `CapturePicture(${targetWindow})`)
        .then(() => setStatus(`Imagem capturada: ${stream.title}`))
        .catch(reason => {
          setError(normalizeHatJsError(reason, 'Não foi possível capturar a imagem desta câmera.'));
        });
    };

    useEffect(() => {
      if (!isPlayback || audioEnabledWindow === null) return;
      if (outOfWindowCells[audioEnabledWindow]) void muteSound();
    }, [isPlayback, audioEnabledWindow, outOfWindowCells]);

    const runPlaybackControl = async (label: string, methodName: string, action: (plugin: HatJsPlugin, windowNumber: number) => Promise<unknown> | unknown) => {
      const plugin = pluginRef.current;
      if (!isPlayback || !ready || !plugin) {
        logHatJs(instanceId, 'control:ignored', { label, methodName, isPlayback, ready, hasPlugin: Boolean(plugin) });
        return false;
      }
      if (controlPendingRef.current) {
        logHatJs(instanceId, 'control:ignored', { label, methodName, reason: 'control-pending' });
        return false;
      }

      controlPendingRef.current = true;
      setControlPending(true);
      const requestId = ++controlRequestRef.current;
      const startedAt = performance.now();
      const windowNumbers = stableStreams
        .map((stream, windowNumber) => ({ stream, windowNumber }))
        .filter(({ stream, windowNumber }) => !stream.playback?.inactive && !outOfWindowCells[windowNumber])
        .map(({ windowNumber }) => windowNumber);
      const skippedWindowNumbers = stableStreams
        .map((stream, windowNumber) => ({ stream, windowNumber }))
        .filter(({ stream, windowNumber }) => stream.playback?.inactive || outOfWindowCells[windowNumber])
        .map(({ windowNumber }) => windowNumber);
      logHatJs(instanceId, 'control:start', { requestId, label, methodName, windowNumbers, skippedWindowNumbers });
      const watchdog = window.setTimeout(() => {
        logHatJs(instanceId, 'control:pending', {
          requestId,
          label,
          methodName,
          windowNumbers,
          elapsedMs: Math.round(performance.now() - startedAt),
        });
      }, CONTROL_WATCHDOG_MS);

      setError(null);
      try {
        if (!windowNumbers.length) {
          if (methodName === 'HATJS_Pause') {
            playbackPausedRef.current = true;
            setPlaybackPaused(true);
          }
          if (methodName === 'HATJS_Resume') {
            playbackPausedRef.current = false;
            setPlaybackPaused(false);
          }
          logHatJs(instanceId, 'control:resolved-without-active-windows', { requestId, label, methodName, skippedWindowNumbers });
          if (requestId === controlRequestRef.current) setStatus(label);
          return true;
        }

        const failedWindows: number[] = [];
        for (const windowNumber of windowNumbers) {
          try {
            const result = action(plugin, windowNumber);
            logHatJs(instanceId, 'control:invoked', {
              requestId,
              label,
              methodName,
              windowNumber,
              returnsPromise: Boolean(result && typeof (result as PromiseLike<unknown>).then === 'function'),
            });
            await Promise.resolve(result);
            logHatJs(instanceId, 'control:window-resolved', { requestId, label, methodName, windowNumber });
          } catch (reason) {
            failedWindows.push(windowNumber);
            logHatJs(instanceId, 'control:window-rejected', {
              requestId,
              label,
              methodName,
              windowNumber,
              reason: normalizeHatJsError(reason, 'rejeição sem detalhes'),
            });
          }
        }

        if (failedWindows.length) {
          const failureMessage = `Não foi possível ${label.toLowerCase()} em ${failedWindows.length} câmera${failedWindows.length === 1 ? '' : 's'}.`;
          logHatJs(instanceId, 'control:partial-failure', { requestId, label, methodName, failedWindows });
          if (requestId === controlRequestRef.current) setError(failureMessage);
          return false;
        }

        if (methodName === 'HATJS_Pause') {
          playbackPausedRef.current = true;
          setPlaybackPaused(true);
        }
        if (methodName === 'HATJS_Resume') {
          playbackPausedRef.current = false;
          setPlaybackPaused(false);
        }
        logHatJs(instanceId, 'control:resolved', {
          requestId,
          label,
          methodName,
          windowNumbers,
          elapsedMs: Math.round(performance.now() - startedAt),
        });
        if (requestId === controlRequestRef.current) setStatus(label);
        return true;
      } catch (reason) {
        logHatJs(instanceId, 'control:rejected', {
          requestId,
          label,
          methodName,
          windowNumbers,
          elapsedMs: Math.round(performance.now() - startedAt),
          reason: normalizeHatJsError(reason, 'rejeição sem detalhes'),
          reasonType: reason === null ? 'null' : typeof reason,
          reasonName: reason instanceof Error ? reason.name : undefined,
        });
        if (requestId === controlRequestRef.current) {
          setError(normalizeHatJsError(reason, `Não foi possível ${label.toLowerCase()}.`));
        }
        return false;
      } finally {
        controlPendingRef.current = false;
        setControlPending(false);
        window.clearTimeout(watchdog);
      }
    };

    const getGlobalAnchorMs = (playbackStreamsList: HatJsStream[]): number | null => {
      const starts = playbackStreamsList
        .filter(stream => !stream.playback?.inactive)
        .map(stream => parsePlaybackWallClock(stream.playback?.startedAt))
        .filter((ms): ms is number => ms !== null);
      return starts.length ? Math.min(...starts) : null;
    };

    const scheduleDeferredWindows = (plugin: HatJsPlugin, streamsList: HatJsStream[]) => {
      deferredWindowsRef.current = new Map();
      const anchorMs = getGlobalAnchorMs(streamsList);
      if (anchorMs === null) return;
      const nextOutOfWindow: Record<number, boolean> = {};
      streamsList.forEach((stream, windowNumber) => {
        if (stream.playback?.inactive) {
          nextOutOfWindow[windowNumber] = true;
          return;
        }
        const recStartMs = parsePlaybackWallClock(stream.playback?.recordingStartedAt);
        if (recStartMs !== null && recStartMs - anchorMs > 1000) {
          deferredWindowsRef.current.set(windowNumber, recStartMs);
          nextOutOfWindow[windowNumber] = true;
          try {
            void Promise.resolve(plugin.HATJS_Pause(windowNumber)).catch(reason =>
              logHatJs(instanceId, 'deferred:pause-rejected', {
                windowNumber,
                reason: normalizeHatJsError(reason, 'rejeição sem detalhes'),
              })
            );
            logHatJs(instanceId, 'deferred:paused', { windowNumber, recordingStartMs: recStartMs, anchorMs });
          } catch (reason) {
            logHatJs(instanceId, 'deferred:pause-rejected', { windowNumber, reason: normalizeHatJsError(reason, 'rejeição sem detalhes') });
          }
        }
      });
      setOutOfWindowCells(nextOutOfWindow);
    };

    const restartLive = async (nextStreams: HatJsStream[], requestedQuality?: HatJsVideoQuality) => {
      const plugin = pluginRef.current;
      if (isPlayback || !ready || !plugin || !nextStreams.length || !activeStreamsRef.current.length) return false;

      const previousStreams = activeStreamsRef.current;
      const previousQuality = qualityRef.current;
      if (requestedQuality) qualityRef.current = requestedQuality;
      const generation = ++playbackGenerationRef.current;
      liveQualitySwitchGenerationRef.current = generation;
      const stopStreams = async (streamCount: number) => {
        for (let windowNumber = 0; windowNumber < streamCount; windowNumber += 1) {
          try {
            await invokeHatJsWithTimeout(() => plugin.HATJS_Stop(windowNumber), `Stop(${windowNumber})`);
          } catch (reason) {
            logHatJs(instanceId, 'live:stop-rejected', {
              generation,
              windowNumber,
              reason: normalizeHatJsError(reason, 'rejeição sem detalhes'),
            });
          }
        }
      };
      const playStreams = async (streamsToPlay: HatJsStream[]) => {
        await Promise.all(
          streamsToPlay.map(async (stream, windowNumber) => {
            logHatJs(instanceId, 'live:play:start', {
              generation,
              windowNumber,
              quality: qualityRef.current,
              streamType: qualityRef.current === 'HIGH' ? 0 : 1,
            });
            await invokeHatJsWithTimeout(() => plugin.HATJS_Play(stream.url, { playURL: stream.url }, windowNumber), `Play(${windowNumber})`);
            logHatJs(instanceId, 'live:play:resolved', {
              generation,
              windowNumber,
              quality: qualityRef.current,
              streamType: qualityRef.current === 'HIGH' ? 0 : 1,
            });
          })
        );
      };

      const audioWindowToRestore = audioEnabledWindow !== null && audioEnabledWindow < nextStreams.length ? audioEnabledWindow : null;
      const restoreAudio = async () => {
        if (audioWindowToRestore === null) return;
        try {
          await invokeHatJsWithTimeout(() => plugin.HATJS_OpenSound(audioWindowToRestore), `OpenSound(${audioWindowToRestore})`);
          setAudioEnabledWindow(audioWindowToRestore);
          setAudioError(null);
          logHatJs(instanceId, 'live:audio-restore:resolved', { generation, windowNumber: audioWindowToRestore });
        } catch (reason) {
          setAudioEnabledWindow(null);
          setAudioError(normalizeHatJsError(reason, 'Não foi possível restaurar o áudio após trocar a qualidade.'));
          logHatJs(instanceId, 'live:audio-restore:rejected', {
            generation,
            windowNumber: audioWindowToRestore,
            reason: normalizeHatJsError(reason, 'rejeição sem detalhes'),
          });
        }
      };

      setError(null);
      if (audioWindowToRestore !== null) setAudioError(null);
      setStatus('Trocando a qualidade da live...');
      setFramesDisplayed({});
      try {
        await stopStreams(Math.max(previousStreams.length, nextStreams.length));
        if (generation !== playbackGenerationRef.current) {
          qualityRef.current = previousQuality;
          return false;
        }
        await playStreams(nextStreams);
        await restoreAudio();
        activeStreamsRef.current = nextStreams;
        if (generation !== playbackGenerationRef.current) {
          activeStreamsRef.current = previousStreams;
          qualityRef.current = previousQuality;
          return false;
        }
        setStatus(
          audioWindowToRestore !== null && nextStreams[audioWindowToRestore]
            ? `Áudio habilitado: ${nextStreams[audioWindowToRestore].title}`
            : 'Áudio silenciado'
        );
        logHatJs(instanceId, 'live:quality-switch:play-resolved', {
          generation,
          quality: qualityRef.current,
          streamType: qualityRef.current === 'HIGH' ? 0 : 1,
          streamCount: nextStreams.length,
        });
        return true;
      } catch (reason) {
        logHatJs(instanceId, 'live:quality-switch:rejected', {
          generation,
          quality: qualityRef.current,
          reason: normalizeHatJsError(reason, 'rejeição sem detalhes'),
        });
        try {
          await stopStreams(nextStreams.length);
          await playStreams(previousStreams);
          await restoreAudio();
          activeStreamsRef.current = previousStreams;
          qualityRef.current = previousQuality;
          setReady(true);
          setStatus('Live anterior restaurada');
          setError('Não foi possível trocar a qualidade; a live anterior foi restaurada.');
          logHatJs(instanceId, 'live:quality-switch:rollback-resolved', { generation, streamCount: previousStreams.length });
        } catch (rollbackReason) {
          activeStreamsRef.current = previousStreams;
          qualityRef.current = previousQuality;
          setError(normalizeHatJsError(rollbackReason, 'Não foi possível trocar a qualidade nem restaurar a live anterior.'));
          logHatJs(instanceId, 'live:quality-switch:rollback-rejected', {
            generation,
            reason: normalizeHatJsError(rollbackReason, 'rejeição sem detalhes'),
          });
        }
        return false;
      } finally {
        setAudioPending(false);
      }
    };

    const restartPlayback = async (nextStreams: HatJsStream[]) => {
      const plugin = pluginRef.current;
      const playbackStreams = nextStreams.filter(stream => stream.playback && !stream.playback.inactive);
      const hasPlaybackRangeForEveryStream = nextStreams.every(stream => stream.playback);
      if (!isPlayback || !ready || !plugin || !nextStreams.length || !playbackStreams.length || !hasPlaybackRangeForEveryStream) return false;

      const generation = ++playbackGenerationRef.current;
      const audioWindowToRestore = audioEnabledWindow !== null && audioEnabledWindow < nextStreams.length ? audioEnabledWindow : null;
      setError(null);
      setStatus('Trocando os trechos do playback...');
      setFramesDisplayed({});
      deferredWindowsRef.current = new Map();
      setOutOfWindowCells({});
      if (audioWindowToRestore !== null) {
        setAudioPending(true);
        setAudioError(null);
      }

      try {
        if (audioWindowToRestore !== null) {
          try {
            await Promise.resolve(plugin.HATJS_CloseSound(audioWindowToRestore));
          } catch (closeReason) {
            logHatJs(instanceId, 'restart:audio-close-rejected', {
              generation,
              windowNumber: audioWindowToRestore,
              reason: normalizeHatJsError(closeReason, 'rejeição sem detalhes'),
            });
          }
        }

        const windowCount = Math.max(stableStreams.length, nextStreams.length);
        for (let windowNumber = 0; windowNumber < windowCount; windowNumber += 1) {
          try {
            await Promise.resolve(plugin.HATJS_Stop(windowNumber));
          } catch (stopReason) {
            logHatJs(instanceId, 'restart:stop-rejected', {
              generation,
              windowNumber,
              reason: normalizeHatJsError(stopReason, 'rejeição sem detalhes'),
            });
          }
        }

        if (generation !== playbackGenerationRef.current) return false;
        await Promise.all(
          nextStreams.map(async (stream, windowNumber) => {
            const playbackRange = stream.playback;
            if (!playbackRange) throw new Error(`Stream sem intervalo de playback na janela ${windowNumber}.`);
            if (playbackRange.inactive) {
              logHatJs(instanceId, 'restart:play:skipped-inactive', { generation, windowNumber });
              return;
            }
            const playStartedAt = performance.now();
            logHatJs(instanceId, 'restart:play:start', { generation, windowNumber });
            try {
              await invokeHatJsWithTimeout(
                () =>
                  plugin.HATJS_Play(
                    stream.url,
                    { playURL: stream.url },
                    windowNumber,
                    toHatPlaybackTime(playbackRange.startedAt),
                    toHatPlaybackTime(playbackRange.endedAt)
                  ),
                `Play(${windowNumber})`
              );
              logHatJs(instanceId, 'restart:play:resolved', { generation, windowNumber, elapsedMs: Math.round(performance.now() - playStartedAt) });
            } catch (reason) {
              logHatJs(instanceId, 'restart:play:rejected', {
                generation,
                windowNumber,
                elapsedMs: Math.round(performance.now() - playStartedAt),
                reason: normalizeHatJsError(reason, 'rejeição sem detalhes'),
              });
              throw reason;
            }
          })
        );
        if (generation !== playbackGenerationRef.current) return false;

        scheduleDeferredWindows(plugin, nextStreams);

        if (audioWindowToRestore !== null) {
          try {
            await Promise.resolve(plugin.HATJS_OpenSound(audioWindowToRestore));
            setAudioEnabledWindow(audioWindowToRestore);
            setAudioError(null);
          } catch (openReason) {
            setAudioEnabledWindow(null);
            setAudioError(normalizeHatJsError(openReason, 'Não foi possível restaurar o áudio após trocar os trechos.'));
            logHatJs(instanceId, 'restart:audio-open-rejected', {
              generation,
              windowNumber: audioWindowToRestore,
              reason: normalizeHatJsError(openReason, 'rejeição sem detalhes'),
            });
          }
        }

        activeStreamsRef.current = nextStreams;

        setPlaybackPaused(false);
        playbackPausedRef.current = false;
        const requestedTime = new Date(playbackStreams[0].playback.startedAt);
        if (Number.isFinite(requestedTime.getTime())) onOsdTime?.(requestedTime);
        setStatus(`${nextStreams.length} câmera${nextStreams.length === 1 ? '' : 's'} de playback conectada${nextStreams.length === 1 ? '' : 's'}`);
        logHatJs(instanceId, 'restart:resolved', {
          generation,
          streamCount: nextStreams.length,
          windowNumbers: nextStreams.map((_stream, windowNumber) => windowNumber),
        });
        return true;
      } catch (reason) {
        logHatJs(instanceId, 'restart:rejected', { generation, reason: normalizeHatJsError(reason, 'rejeição sem detalhes') });
        setError(normalizeHatJsError(reason, 'Não foi possível reposicionar os playbacks.'));
        return false;
      } finally {
        if (audioWindowToRestore !== null) setAudioPending(false);
      }
    };

    useImperativeHandle(ref, () => ({ restartLive, restartPlayback }), [isPlayback, ready, onOsdTime, playbackStream, audioEnabledWindow, stableStreams]);

    const resumePlayback = async () => {
      await runPlaybackControl('Playback retomado', 'HATJS_Resume', (plugin, windowNumber) => {
        if (deferredWindowsRef.current.has(windowNumber)) {
          logHatJs(instanceId, 'deferred:resume-skipped-on-global-resume', { windowNumber });
          return undefined;
        }
        return plugin.HATJS_Resume(windowNumber);
      });
    };

    const clearRecordingTimeout = () => {
      if (recordingTimeoutRef.current !== null) {
        window.clearTimeout(recordingTimeoutRef.current);
        recordingTimeoutRef.current = null;
      }
    };

    const stopWindowRecording = async (targetWindow: number, stream: HatJsStream, automatic = false) => {
      if (recordingWindowRef.current !== targetWindow) return;
      if (recordingOperationRef.current) {
        if (automatic) {
          recordingTimeoutRef.current = window.setTimeout(() => {
            recordingTimeoutRef.current = null;
            void stopWindowRecording(targetWindow, stream, true);
          }, 1000);
        }
        return;
      }

      const plugin = pluginRef.current;
      if (!plugin?.HATJS_StopSave) return;

      recordingOperationRef.current = true;
      setRecordingPending(true);
      try {
        await invokeHatJsWithTimeout(() => plugin.HATJS_StopSave?.(targetWindow), `StopSave(${targetWindow})`);
        clearRecordingTimeout();
        recordingWindowRef.current = null;
        setRecordingWindow(null);
        setStatus(automatic ? `Gravação encerrada automaticamente após 2 minutos: ${stream.title}` : `Gravação finalizada: ${stream.title}`);
      } catch (reason) {
        setError(normalizeHatJsError(reason, 'Não foi possível finalizar a gravação.'));
      } finally {
        recordingOperationRef.current = false;
        setRecordingPending(false);
      }
    };

    const toggleWindowRecording = async (targetWindow: number) => {
      const plugin = pluginRef.current;
      const stream = stableStreams[targetWindow];
      if (!ready || !plugin || !stream || recordingOperationRef.current) return;

      if (recordingWindowRef.current === targetWindow) {
        await stopWindowRecording(targetWindow, stream);
        return;
      }
      if (recordingWindowRef.current !== null) return;
      if (!plugin.HATJS_StartSave || !plugin.HATJS_StopSave) {
        setError('O player não permite gravar esta câmera.');
        return;
      }

      recordingOperationRef.current = true;
      setRecordingPending(true);
      setError(null);

      try {
        const safeTitle = stream.title
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .replace(/[^a-zA-Z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '')
          .toLowerCase();
        const timestamp = new Date()
          .toISOString()
          .replace(/[-:T.Z]/g, '')
          .slice(0, 14);
        const fileName = `camera-${safeTitle || stream.channelId}-${timestamp}.mp4`;

        await invokeHatJsWithTimeout(() => plugin.HATJS_StartSave?.(targetWindow, fileName, { iPackage: 0 }), `StartSave(${targetWindow})`);
        recordingWindowRef.current = targetWindow;
        setRecordingWindow(targetWindow);
        setStatus(`Gravando: ${stream.title}`);
        recordingTimeoutRef.current = window.setTimeout(() => {
          recordingTimeoutRef.current = null;
          void stopWindowRecording(targetWindow, stream, true);
        }, HATJS_RECORDING_MAX_DURATION_MS);
      } catch (reason) {
        setError(normalizeHatJsError(reason, 'Não foi possível gravar esta câmera.'));
      } finally {
        recordingOperationRef.current = false;
        setRecordingPending(false);
      }
    };

    useEffect(() => {
      let disposed = false;
      let startPromise: Promise<void> = Promise.resolve();
      const container = containerRef.current;
      let removeResizeListener: (() => void) | undefined;
      let readinessTimer: number | null = null;
      let readinessFallbackTimer: number | null = null;
      let osdTimer: number | null = null;
      let osdErrorLogged = false;
      let osdDriftLogged = false;
      let readinessOsdUnavailable = false;
      let controlsReady = false;

      setError(null);
      setAudioError(null);
      setReady(false);
      controlPendingRef.current = false;
      setControlPending(false);
      setPlaybackPaused(false);
      playbackPausedRef.current = false;
      playbackGenerationRef.current += 1;
      setAudioWindow(0);
      setAudioEnabledWindow(null);
      setAudioWasActivated(false);
      setFramesDisplayed({});
      deferredWindowsRef.current = new Map();
      setOutOfWindowCells({});

      const start = async () => {
        if (!container || !stableStreams.length) {
          logHatJs(instanceId, 'start:ignored', { hasContainer: Boolean(container), streamCount: stableStreams.length });
          return;
        }
        try {
          if (disposed) return;
          setStatus('Carregando player de vídeo...');
          logHatJs(instanceId, 'sdk:load:start', { isPlayback, streamCount: stableStreams.length });
          await loadHatJsSdk();
          logHatJs(instanceId, 'sdk:load:resolved');
          if (disposed) return;

          const HatMediaJsPlugin = window.HATMediaJsPlugin;
          if (!HatMediaJsPlugin) {
            throw new Error('O recurso de vídeo não foi disponibilizado pelo player.');
          }

          const width = Math.max(Math.round(container.getBoundingClientRect().width), 100);
          const height = Math.max(Math.round(container.getBoundingClientRect().height), 100);
          const playerBorderColor = alpha(theme.palette.common.black, theme.palette.mode === 'dark' ? 0.55 : 0.68);
          const playerSelectedBorderColor = 'transparent';
          const playerBackgroundColor = theme.palette.common.black;
          const plugin = new HatMediaJsPlugin({
            szId: container.id,
            iType: 2,
            iMode: 0,
            iWidth: width,
            iHeight: height,
            iMaxSplit: split,
            iCurrentSplit: split,
            szBasePath: HAT_PLUGIN_BASE_PATH,
            oStyle: { border: playerBorderColor, borderSelect: playerSelectedBorderColor, background: playerBackgroundColor },
          });
          pluginRef.current = plugin;
          logHatJs(instanceId, 'plugin:created', { width, height, split, isPlayback });
          const markControlsReady = (reason: string) => {
            if (disposed || controlsReady) return;
            controlsReady = true;
            if (readinessTimer !== null) window.clearTimeout(readinessTimer);
            if (readinessFallbackTimer !== null) window.clearTimeout(readinessFallbackTimer);
            setReady(true);
            logHatJs(instanceId, 'player:ready', { isPlayback, streamCount: stableStreams.length, reason });
            setStatus(isPlayback ? 'Playback conectado' : 'Câmeras conectadas — selecione o áudio de uma janela');
            void activateSound();
          };
          plugin.HATJS_SetWindowControlCallback?.({
            pluginErrorHandler(_windowNumber, code, data) {
              logHatJs(instanceId, 'plugin:error', { windowNumber: _windowNumber, code, data });
              if (!disposed) {
                setError(playerStreamErrorMessage(data?.statusString || data?.subStatusCode?.toString()));
              }
            },
            onFirstFrameDisplay(windowNumber, frameWidth, frameHeight) {
              logHatJs(instanceId, 'plugin:first-frame', {
                windowNumber,
                width: frameWidth,
                height: frameHeight,
                quality: qualityRef.current,
                displayQuality: qualityRef.current === 'HIGH' ? 'HD' : 'SD',
                streamType: qualityRef.current === 'HIGH' ? 0 : 1,
                qualitySwitchGeneration: liveQualitySwitchGenerationRef.current,
              });
              if (!disposed) setFramesDisplayed(current => ({ ...current, [windowNumber]: true }));
              markControlsReady('first-frame');
            },
          });

          const probeControlsReadiness = async () => {
            if (disposed || controlsReady || readinessOsdUnavailable) return;
            try {
              const currentMasterWindow = getPlaybackMaster(activeStreamsRef.current)?.windowNumber ?? playbackMasterWindow;
              const osdValue = plugin.HATJS_GetOSDTime ? await Promise.resolve(plugin.HATJS_GetOSDTime(currentMasterWindow)) : null;
              const osdTime = parseHatJsOsdTime(osdValue);
              if (osdTime) {
                logHatJs(instanceId, 'readiness:osd-time', { osdTime: osdTime.toISOString() });
                markControlsReady('osd-time');
                return;
              }
            } catch (reason) {
              readinessOsdUnavailable = true;
              logHatJs(instanceId, 'readiness:osd-unavailable', { reason: normalizeHatJsError(reason, 'OSD ainda não disponível') });
            }
            if (!disposed && !controlsReady && !readinessOsdUnavailable) {
              readinessTimer = window.setTimeout(() => void probeControlsReadiness(), 500);
            }
          };

          const pollPlaybackTime = async () => {
            if (disposed || !isPlayback) return;
            if (playbackPausedRef.current) {
              osdTimer = window.setTimeout(() => void pollPlaybackTime(), OSD_POLL_INTERVAL_MS);
              return;
            }
            const generation = playbackGenerationRef.current;
            try {
              if (plugin.HATJS_GetOSDTime) {
                const currentStreams = activeStreamsRef.current;
                const currentPlaybackMaster = getPlaybackMaster(currentStreams);
                const currentPlaybackWindowNumbers = currentStreams.reduce<number[]>((windowNumbers, stream, windowNumber) => {
                  if (stream.playback && !stream.playback.inactive) windowNumbers.push(windowNumber);
                  return windowNumbers;
                }, []);
                const osdTimes: Array<{ windowNumber: number; date: Date }> = [];
                for (const windowNumber of currentPlaybackWindowNumbers) {
                  try {
                    const osdValue = await Promise.resolve(plugin.HATJS_GetOSDTime(windowNumber));
                    const osdTime = parseHatJsOsdTime(osdValue);
                    if (osdTime) osdTimes.push({ windowNumber, date: osdTime });
                  } catch (reason) {
                    if (!osdErrorLogged) {
                      osdErrorLogged = true;
                      logHatJs(instanceId, 'osd:rejected', {
                        windowNumber,
                        reason: normalizeHatJsError(reason, 'OSD indisponível durante o playback'),
                      });
                    }
                  }
                }

                const masterWindowNumber = currentPlaybackMaster?.windowNumber ?? playbackMasterWindow;
                const masterOsd = osdTimes.find(osd => osd.windowNumber === masterWindowNumber);
                if (generation === playbackGenerationRef.current && !playbackPausedRef.current && masterOsd) {
                  osdErrorLogged = false;
                  onOsdTime?.(masterOsd.date);

                  const masterMs = masterOsd.date.getTime();
                  if (deferredWindowsRef.current.size) {
                    for (const [windowNumber, resumeAtMs] of Array.from(deferredWindowsRef.current.entries())) {
                      if (masterMs >= resumeAtMs) {
                        deferredWindowsRef.current.delete(windowNumber);
                        try {
                          void Promise.resolve(plugin.HATJS_Resume(windowNumber)).catch(reason =>
                            logHatJs(instanceId, 'deferred:resume-rejected', {
                              windowNumber,
                              reason: normalizeHatJsError(reason, 'rejeição sem detalhes'),
                            })
                          );
                          logHatJs(instanceId, 'deferred:resumed', { windowNumber, masterMs, resumeAtMs });
                        } catch (reason) {
                          logHatJs(instanceId, 'deferred:resume-rejected', {
                            windowNumber,
                            reason: normalizeHatJsError(reason, 'rejeição sem detalhes'),
                          });
                        }
                      }
                    }
                  }

                  setOutOfWindowCells(previous => {
                    const next: Record<number, boolean> = {};
                    currentStreams.forEach((stream, windowNumber) => {
                      const recStartMs = parsePlaybackWallClock(stream.playback?.recordingStartedAt);
                      const recEndMs = parsePlaybackWallClock(stream.playback?.recordingEndedAt);
                      const beforeStart = recStartMs !== null && masterMs < recStartMs - 1000;
                      const afterEnd = recEndMs !== null && masterMs > recEndMs + 1000;
                      if (beforeStart || afterEnd) next[windowNumber] = true;
                    });
                    const keys = new Set([...Object.keys(previous), ...Object.keys(next)]);
                    let changed = false;
                    keys.forEach(key => {
                      if (Boolean(previous[Number(key)]) !== Boolean(next[Number(key)])) changed = true;
                    });
                    return changed ? next : previous;
                  });
                }
                if (masterOsd && osdTimes.length > 1) {
                  const maximumDriftMs = Math.max(...osdTimes.map(osd => Math.abs(osd.date.getTime() - masterOsd.date.getTime())));
                  if (maximumDriftMs >= 1000) {
                    if (!osdDriftLogged) {
                      logHatJs(instanceId, 'osd:drift', {
                        masterWindow: masterWindowNumber,
                        maximumDriftMs,
                        osdTimes: osdTimes.map(osd => ({ windowNumber: osd.windowNumber, osdTime: osd.date.toISOString() })),
                      });
                    }
                    osdDriftLogged = true;
                  } else {
                    osdDriftLogged = false;
                  }
                }
              }
            } catch (reason) {
              if (!osdErrorLogged) {
                osdErrorLogged = true;
                logHatJs(instanceId, 'osd:rejected', { reason: normalizeHatJsError(reason, 'OSD indisponível durante o playback') });
              }
            }
            if (!disposed && isPlayback) {
              osdTimer = window.setTimeout(() => void pollPlaybackTime(), OSD_POLL_INTERVAL_MS);
            }
          };

          let resizeFrame: number | null = null;
          const resize = () => {
            if (resizeFrame !== null) return;
            resizeFrame = window.requestAnimationFrame(() => {
              resizeFrame = null;
              if (disposed) return;
              try {
                const currentWidth = Math.max(Math.round(container.getBoundingClientRect().width), 100);
                const currentHeight = Math.max(Math.round(container.getBoundingClientRect().height), 100);
                void Promise.resolve(plugin.HATJS_Resize?.(currentWidth, currentHeight)).catch(reason => {
                  if (!disposed) setError(normalizeHatJsError(reason, 'Não foi possível ajustar o tamanho do player.'));
                });
              } catch (reason) {
                if (!disposed) setError(normalizeHatJsError(reason, 'Não foi possível ajustar o tamanho do player.'));
              }
            });
          };
          const resizeObserver = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(resize);
          resizeObserver?.observe(container);
          window.addEventListener('resize', resize);
          removeResizeListener = () => {
            window.removeEventListener('resize', resize);
            resizeObserver?.disconnect();
            if (resizeFrame !== null) window.cancelAnimationFrame(resizeFrame);
          };
          resize();

          const cameraCountLabel = ['uma', 'duas', 'três', 'quatro'][stableStreams.length - 1] ?? String(stableStreams.length);
          setStatus(`${isPlayback ? 'Conectando playback' : `Conectando ${cameraCountLabel} câmera${stableStreams.length === 1 ? '' : 's'}`}...`);
          await Promise.all(
            stableStreams.map(async (stream, windowNumber) => {
              if (disposed) return;
              const playStartedAt = performance.now();
              const playback = stream.playback;
              logHatJs(instanceId, 'play:start', {
                windowNumber,
                playback: Boolean(playback),
                quality: qualityRef.current,
                streamType: qualityRef.current === 'HIGH' ? 0 : 1,
                startedAt: playback?.startedAt,
                endedAt: playback?.endedAt,
              });
              try {
                if (playback) {
                  if (playback.inactive) {
                    logHatJs(instanceId, 'play:skipped-inactive', { windowNumber });
                    return;
                  }
                  let lastReason: unknown = null;
                  let played = false;
                  for (let attempt = 1; attempt <= HATJS_PLAYBACK_PLAY_MAX_ATTEMPTS && !played && !disposed; attempt += 1) {
                    try {
                      if (attempt > 1) {
                        logHatJs(instanceId, 'play:retry', { windowNumber, attempt });
                        try {
                          await invokeHatJsWithTimeout(() => plugin.HATJS_Stop(windowNumber), `Stop(${windowNumber})`);
                        } catch (stopReason) {
                          logHatJs(instanceId, 'play:retry-stop-rejected', {
                            windowNumber,
                            attempt,
                            reason: normalizeHatJsError(stopReason, 'rejeição sem detalhes'),
                          });
                        }
                      }
                      await invokeHatJsWithTimeout(
                        () =>
                          plugin.HATJS_Play(
                            stream.url,
                            { playURL: stream.url },
                            windowNumber,
                            toHatPlaybackTime(playback.startedAt),
                            toHatPlaybackTime(playback.endedAt)
                          ),
                        `Play(${windowNumber})`,
                        HATJS_PLAYBACK_PLAY_TIMEOUT_MS
                      );
                      played = true;
                    } catch (attemptReason) {
                      lastReason = attemptReason;
                      logHatJs(instanceId, 'play:attempt-rejected', {
                        windowNumber,
                        attempt,
                        elapsedMs: Math.round(performance.now() - playStartedAt),
                        reason: normalizeHatJsError(attemptReason, 'rejeição sem detalhes'),
                      });
                    }
                  }
                  if (!played) throw lastReason ?? new Error('Não foi possível abrir o vídeo desta câmera.');
                } else {
                  let lastReason: unknown = null;
                  let played = false;
                  for (let attempt = 1; attempt <= HATJS_LIVE_PLAY_MAX_ATTEMPTS && !played && !disposed; attempt += 1) {
                    try {
                      if (attempt > 1) {
                        logHatJs(instanceId, 'live:play:retry', { windowNumber, attempt });
                        try {
                          await invokeHatJsWithTimeout(() => plugin.HATJS_Stop(windowNumber), `Stop(${windowNumber})`);
                        } catch (stopReason) {
                          logHatJs(instanceId, 'live:play:retry-stop-rejected', {
                            windowNumber,
                            attempt,
                            reason: normalizeHatJsError(stopReason, 'rejeição sem detalhes'),
                          });
                        }
                      }
                      await invokeHatJsWithTimeout(
                        () => plugin.HATJS_Play(stream.url, { playURL: stream.url }, windowNumber),
                        `Play(${windowNumber})`,
                        HATJS_LIVE_PLAY_TIMEOUT_MS
                      );
                      played = true;
                    } catch (attemptReason) {
                      lastReason = attemptReason;
                      logHatJs(instanceId, 'live:play:attempt-rejected', {
                        windowNumber,
                        attempt,
                        elapsedMs: Math.round(performance.now() - playStartedAt),
                        reason: normalizeHatJsError(attemptReason, 'rejeição sem detalhes'),
                      });
                    }
                  }
                  if (!played) throw lastReason ?? new Error('Não foi possível abrir o vídeo desta câmera.');
                }
                logHatJs(instanceId, 'play:resolved', { windowNumber, elapsedMs: Math.round(performance.now() - playStartedAt) });
              } catch (reason) {
                logHatJs(instanceId, 'play:rejected', {
                  windowNumber,
                  elapsedMs: Math.round(performance.now() - playStartedAt),
                  reason: normalizeHatJsError(reason, 'rejeição sem detalhes'),
                });
                throw reason;
              }
            })
          );
          if (!disposed && isPlayback) {
            scheduleDeferredWindows(plugin, stableStreams);
          }
          if (!disposed) {
            if (isPlayback) {
              const initialPlaybackTime = playbackStream?.playback ? new Date(playbackStream.playback.startedAt) : null;
              if (initialPlaybackTime && Number.isFinite(initialPlaybackTime.getTime())) onOsdTime?.(initialPlaybackTime);
              setStatus('Playback conectando...');
              void pollPlaybackTime();
              void probeControlsReadiness();
              readinessFallbackTimer = window.setTimeout(() => {
                if (!disposed && !controlsReady) {
                  logHatJs(instanceId, 'readiness:fallback', { reason: 'play-resolved-grace-period', delayMs: CONTROL_READINESS_FALLBACK_MS });
                  markControlsReady('play-resolved-fallback');
                }
              }, CONTROL_READINESS_FALLBACK_MS);
            } else {
              markControlsReady('play-resolved');
            }
          }
        } catch (startError) {
          logHatJs(instanceId, 'start:rejected', { reason: normalizeHatJsError(startError, 'erro sem detalhes') });
          if (!disposed) {
            setError(playerStreamErrorMessage(startError));
            setStatus(`Falha ao iniciar ${isPlayback ? 'playback' : 'live'}`);
          }
        }
      };

      startPromise = enqueueHatJsLifecycle(start);

      return () => {
        disposed = true;
        controlRequestRef.current += 1;
        if (recordingTimeoutRef.current !== null) {
          window.clearTimeout(recordingTimeoutRef.current);
          recordingTimeoutRef.current = null;
        }
        if (readinessTimer !== null) window.clearTimeout(readinessTimer);
        if (readinessFallbackTimer !== null) window.clearTimeout(readinessFallbackTimer);
        if (osdTimer !== null) window.clearTimeout(osdTimer);
        logHatJs(instanceId, 'cleanup:start', { isPlayback, streamCount: stableStreams.length });
        removeResizeListener?.();
        const cleanup = async () => {
          const plugin = pluginRef.current;
          if (plugin) {
            for (let windowNumber = 0; windowNumber < stableStreams.length; windowNumber += 1) {
              if (recordingWindowRef.current === windowNumber && plugin.HATJS_StopSave) {
                try {
                  await invokeHatJsWithTimeout(() => plugin.HATJS_StopSave?.(windowNumber), `StopSave(${windowNumber})`);
                } catch (cleanupError) {
                  logHatJs(instanceId, 'cleanup:stop-save:rejected', {
                    windowNumber,
                    reason: normalizeHatJsError(cleanupError, 'rejeição sem detalhes'),
                  });
                }
              }
              try {
                await invokeHatJsWithTimeout(() => plugin.HATJS_CloseSound(windowNumber), `CloseSound(${windowNumber})`);
              } catch (cleanupError) {
                logHatJs(instanceId, 'cleanup:close-sound:rejected', {
                  windowNumber,
                  reason: normalizeHatJsError(cleanupError, 'rejeição sem detalhes'),
                });
              }
              try {
                await invokeHatJsWithTimeout(() => plugin.HATJS_Stop(windowNumber), `Stop(${windowNumber})`);
              } catch (cleanupError) {
                logHatJs(instanceId, 'cleanup:stop:rejected', {
                  windowNumber,
                  reason: normalizeHatJsError(cleanupError, 'rejeição sem detalhes'),
                });
              }
            }
            try {
              logHatJs(instanceId, 'cleanup:destroy:start');
              await invokeHatJsWithTimeout(() => plugin.HATJS_DestroyPlugin(), 'DestroyPlugin');
              logHatJs(instanceId, 'cleanup:destroy:resolved');
            } catch (cleanupError) {
              logHatJs(instanceId, 'cleanup:destroy:rejected', { reason: normalizeHatJsError(cleanupError, 'rejeição sem detalhes') });
            }
            if (pluginRef.current === plugin) pluginRef.current = null;
          }
          container?.replaceChildren();
        };
        hatJsLifecycleQueue = hatJsLifecycleQueue
          .then(async () => {
            await startPromise.catch(() => undefined);
            await cleanup();
          })
          .catch(cleanupError => {
            logHatJs(instanceId, 'cleanup:queue-rejected', { reason: normalizeHatJsError(cleanupError, 'rejeição sem detalhes') });
          });
      };
    }, [stableStreams, split, isPlayback]);

    const activeIssue = error || audioError;
    const displayedMessage = activeIssue ? shortErrorMessage(activeIssue) : status;
    const isConnected = ready && !error;

    return (
      <Box
        ref={rootRef}
        onMouseMove={revealControls}
        onMouseEnter={revealControls}
        onMouseLeave={scheduleControlsHide}
        onTouchStart={revealControls}
        onFocusCapture={revealControls}
        tabIndex={overlayControls ? 0 : undefined}
        sx={{
          position: overlayControls ? 'relative' : 'static',
          width: '100%',
          height: preserveVideoAspectRatio && !isFullscreen ? 'auto' : '100%',
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: 'background.paper',
          borderRadius: 1,
          overflow: 'hidden',
          boxSizing: 'border-box',
        }}
        aria-label={title}
      >
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            flex: preserveVideoAspectRatio && !isFullscreen ? '0 0 auto' : '1 1 auto',
            minHeight: 0,
            aspectRatio: isFullscreen ? 'auto' : '16 / 9',
            backgroundColor: theme.palette.common.black,
          }}
        >
          <Box
            ref={containerRef}
            id={containerId}
            sx={{
              width: '100%',
              height: '100%',
              transform: spotlight.transform,
              transformOrigin: spotlight.origin,
              transition: 'transform 220ms ease',
              willChange: spotlight.willChange,
            }}
          />

          <HatJsVideoOverlay
            streams={stableStreams}
            gridColumns={gridColumns}
            gridRows={gridRows}
            isPlayback={isPlayback}
            outOfWindowCells={outOfWindowCells}
            audioEnabledWindow={audioEnabledWindow}
            audioPending={audioPending}
            ready={ready}
            recordingWindow={recordingWindow}
            recordingPending={recordingPending}
            enableSpotlightMode={enableSpotlightMode}
            spotlightIsActive={spotlight.isActive}
            spotlightWindowIndex={spotlight.index}
            onAudioWindowToggle={toggleAudioWindow}
            onSpotlightChange={spotlight.setChannelId}
            onWindowFullscreen={openWindowFullscreen}
            onWindowSnapshot={captureWindowSnapshot}
            onWindowRecordToggle={toggleWindowRecording}
          />

          {isInitializing && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                zIndex: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1.5,
                backgroundColor: 'rgba(0,0,0,0.55)',
                pointerEvents: 'none',
              }}
            >
              <CircularProgress size={36} thickness={4} sx={{ color: 'rgba(255,255,255,0.9)' }} />
              <Typography variant="body2" sx={{ color: '#fff', textAlign: 'center', px: 2 }}>
                {status}
              </Typography>
            </Box>
          )}

          {error && (
            <Box sx={{ position: 'absolute', inset: 0, zIndex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
              <Chip
                icon={<ErrorOutlineRoundedIcon />}
                label={shortErrorMessage(error)}
                title={error}
                sx={{
                  maxWidth: '100%',
                  height: 'auto',
                  py: 0.5,
                  backgroundColor: alpha('#7f1d1d', 0.9),
                  color: '#fff',
                  '& .MuiChip-label': { whiteSpace: 'normal', textAlign: 'center' },
                  '& .MuiChip-icon': { color: '#fca5a5' },
                }}
              />
            </Box>
          )}
        </Box>

        {(!overlayControls || ready) && (
          <Stack
            role="toolbar"
            aria-label="Controles do player de vídeo"
            onMouseEnter={keepControlsVisible}
            onMouseMove={keepControlsVisible}
            onMouseLeave={scheduleControlsHide}
            spacing={0.5}
            sx={getHatJsToolbarSx(overlayControls, controlsVisible, Boolean(controls))}
          >
            <Stack direction="row" alignItems="center" flexWrap="nowrap" gap={controls ? 0.75 : 1} sx={{ minHeight: controls ? 'unset' : 36, minWidth: 0 }}>
              {isPlayback && (
                <>
                  <Stack direction="row" spacing={0.25} role="group" aria-label="Transporte do playback">
                    <Tooltip title={playbackPaused ? 'Retomar playback' : 'Pausar playback'}>
                      <span>
                        <IconButton
                          size="small"
                          color="inherit"
                          aria-label={playbackPaused ? 'Retomar playback' : 'Pausar playback'}
                          onClick={() =>
                            playbackPaused
                              ? void resumePlayback()
                              : void runPlaybackControl('Playback pausado', 'HATJS_Pause', (plugin, windowNumber) => plugin.HATJS_Pause(windowNumber))
                          }
                          disabled={!ready || controlPending}
                          sx={getHatJsOutlinedControlSx(overlayControls)}
                        >
                          {playbackPaused ? <PlayArrowRoundedIcon fontSize="small" /> : <PauseRoundedIcon fontSize="small" />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                </>
              )}

              <HatJsQualityControl
                show={showQualityControl}
                quality={quality}
                qualityChanging={qualityChanging}
                qualityChangeDisabled={qualityChangeDisabled}
                ready={ready}
                overlayControls={overlayControls}
                instanceId={instanceId}
                onQualityChange={onQualityChange}
              />

              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flex: '0 0 auto' }} role="group" aria-label="Áudio">
                {stableStreams.length > 1 && !enableSpotlightMode && (
                  <>
                    <Tooltip title="Selecionar câmera de áudio">
                      <span>
                        <ButtonBase
                          aria-label="Selecionar câmera de áudio"
                          disabled={!ready || audioPending}
                          onClick={event => setAudioAnchorEl(event.currentTarget)}
                          sx={{
                            height: 28,
                            px: 1,
                            gap: 0.5,
                            borderRadius: '6px',
                            border: 1,
                            borderColor: overlayControls ? 'rgba(255,255,255,0.55)' : 'divider',
                            backgroundColor: 'transparent',
                            color: overlayControls ? '#fff' : 'text.primary',
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            whiteSpace: 'nowrap',
                            transition: theme.transitions.create(['background-color', 'border-color']),
                            '&:hover': {
                              backgroundColor: overlayControls ? 'rgba(255,255,255,0.16)' : 'action.hover',
                              borderColor: overlayControls ? '#fff' : 'text.secondary',
                            },
                            '&.Mui-disabled': { opacity: 0.4 },
                          }}
                        >
                          {stableStreams[Math.min(audioWindow, stableStreams.length - 1)]?.title ?? 'Câmera'}
                          <KeyboardArrowDownRoundedIcon sx={{ fontSize: 14, color: overlayControls ? 'rgba(255,255,255,0.75)' : 'text.secondary' }} />
                        </ButtonBase>
                      </span>
                    </Tooltip>
                    <Popper
                      open={Boolean(audioAnchorEl)}
                      anchorEl={audioAnchorEl}
                      placement="top-start"
                      container={audioPopoverContainer}
                      style={{ zIndex: theme.zIndex.modal + 1 }}
                    >
                      <ClickAwayListener onClickAway={() => setAudioAnchorEl(null)}>
                        <Paper sx={{ borderRadius: '8px', minWidth: 140, py: 0.5, boxShadow: 4 }}>
                          {stableStreams.map((stream, index) => {
                            const cameraOutOfWindow = isPlayback && Boolean(outOfWindowCells[index]);
                            return (
                              <MenuItem
                                key={stream.channelId}
                                selected={audioWindow === index}
                                disabled={cameraOutOfWindow}
                                onClick={() => {
                                  setAudioAnchorEl(null);
                                  void changeAudioWindow(index);
                                }}
                                sx={{ fontSize: '0.8125rem', minHeight: 36 }}
                              >
                                {stream.title}
                                {cameraOutOfWindow && ' (sem imagem)'}
                              </MenuItem>
                            );
                          })}
                        </Paper>
                      </ClickAwayListener>
                    </Popper>
                  </>
                )}
                <Tooltip title={getHatJsAudioLabel(audioEnabledWindow !== null, audioWasActivated)}>
                  <span>
                    <IconButton
                      size="small"
                      color="inherit"
                      aria-label={getHatJsAudioAriaLabel(audioEnabledWindow !== null)}
                      onClick={toggleSound}
                      disabled={!ready || audioPending}
                      sx={{
                        borderRadius: '6px',
                        width: 28,
                        height: 28,
                        color: getHatJsAudioIconColor(overlayControls, audioEnabledWindow !== null, audioWasActivated),
                        '&:hover': { backgroundColor: overlayControls ? 'rgba(255,255,255,0.16)' : undefined },
                      }}
                    >
                      {getHatJsAudioIcon(audioEnabledWindow !== null, audioWasActivated)}
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>

              {controls ? (
                <>
                  <Divider orientation="vertical" flexItem sx={{ borderColor: 'divider' }} />
                  <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden', alignSelf: 'stretch', display: 'flex', alignItems: 'center' }}>{controls}</Box>
                </>
              ) : (
                <>
                  <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flex: '1 1 auto', minWidth: 0, overflow: 'hidden' }}>
                    {activeIssue && <ErrorOutlineRoundedIcon sx={{ fontSize: 16, color: 'error.main', flexShrink: 0 }} />}
                    <Typography
                      component="span"
                      role={activeIssue ? 'alert' : 'status'}
                      aria-live="polite"
                      variant="caption"
                      sx={{
                        minWidth: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        color: activeIssue ? 'error.main' : 'text.secondary',
                      }}
                      title={activeIssue || status}
                    >
                      {displayedMessage}
                    </Typography>
                  </Stack>
                  <Chip
                    size="small"
                    variant="outlined"
                    label={getHatJsStatusLabel(isConnected, Boolean(error), isLive)}
                    icon={
                      <Box
                        component="span"
                        sx={{
                          width: 7,
                          height: 7,
                          borderRadius: '50%',
                          flexShrink: 0,
                          backgroundColor: getHatJsStatusDotColor(isConnected, Boolean(error)),
                          ...(isConnected && {
                            animation: 'hatJsStatusPulse 1.6s ease-in-out infinite',
                            '@keyframes hatJsStatusPulse': {
                              '0%, 100%': { opacity: 1 },
                              '50%': { opacity: 0.3 },
                            },
                          }),
                        }}
                      />
                    }
                    sx={{
                      flexShrink: 0,
                      ...(isConnected && {
                        color: '#fff',
                        borderColor: 'rgba(255,255,255,0.28)',
                        backgroundColor: 'rgba(0,0,0,0.48)',
                      }),
                      '& .MuiChip-icon': { ml: 1, mr: 0 },
                      '& .MuiChip-label': { pr: 1.25 },
                    }}
                  />
                  {onFullscreenToggle && (
                    <>
                      <Box
                        component="span"
                        sx={{
                          width: '1px',
                          height: '24px',
                          backgroundColor: 'rgba(255,255,255,0.28)',
                          flexShrink: 0,
                        }}
                      />
                      <Tooltip title={isFullscreen ? 'Sair de tela cheia' : 'Tela cheia'}>
                        <span>
                          <IconButton
                            onClick={() => {
                              spotlight.setChannelId(null);
                              onFullscreenToggle(!isFullscreen);
                            }}
                            aria-label={isFullscreen ? 'Sair de tela cheia' : 'Tela cheia'}
                            size="small"
                            sx={{
                              borderRadius: '6px',
                              width: 28,
                              height: 28,
                              color: '#fff',
                              '&:hover': { backgroundColor: 'rgba(255,255,255,0.16)' },
                            }}
                          >
                            {isFullscreen ? <FullscreenExitRoundedIcon fontSize="medium" /> : <FullscreenRoundedIcon fontSize="medium" />}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </>
                  )}
                </>
              )}
            </Stack>
          </Stack>
        )}
      </Box>
    );
  }
);

HatJsVideoPlayer.displayName = 'HatJsVideoPlayer';
